/**
 * Typed fetch client for the Murmuration BFF.
 *
 * The generated `src/api/zod.gen.ts` (excluded from tsconfig) is the
 * authoritative shape; this file restates the types the SPA actually
 * touches so app code can import them without dragging the auto-generated
 * tree into the build graph. When the upstream OpenAPI changes, regenerate
 * the zod module via `npm run gen:api` and update the structural types
 * below to match.
 *
 * Authentication: every request carries `Authorization: Bearer <access
 * token>` acquired silently via MSAL. The token-acquirer is injected as a
 * dependency so the SPA can wire MSAL while tests can wire a constant
 * stub. The dependency injection seam is what keeps the M2 issuer-swap
 * (ADR-008) a single-file change.
 *
 * Error mapping: the client raises a single `BffError` subclass that
 * carries the HTTP status, the BFF correlation id (from
 * `x-correlation-id`), and the upstream error envelope when one is
 * present. TanStack Query's `onError` and `useBffError` consume that.
 */

import type { InteractionRequiredAuthError } from '@azure/msal-browser'

// ---------------------------------------------------------------------------
// Types - mirror of the OpenAPI schemas the SPA renders.
// ---------------------------------------------------------------------------

/**
 * SM2 projection of a legacy account. Field-for-field equivalent to the
 * `Account` schema in `account-service.yaml`. PII-bearing fields
 * (`legacy_username`, `bank_account_number`, `sort_code`, `iban`,
 * `tribe_epm_id`) are typed nullable but never rendered on the list view;
 * they live on the detail page (PR-E2) behind `MaskedField`.
 */
export interface Account {
  readonly id: string
  readonly legacy_id: string | number
  readonly ext_account_id: string
  readonly legacy_username: string
  readonly provider: string
  readonly tribe_epm_id: string | null
  readonly bank_account_number: string | null
  readonly sort_code: string | null
  readonly iban: string | null
  readonly account_currency: string
  readonly region: string | null
  readonly product_class_id: string
  readonly time_zone: string | null
  readonly account_status: string | null
  readonly external_banking_status: string | null
  readonly last_dormancy_assessed_at: string | null
  readonly first_load_at: string | null
  readonly legacy_created_at: string
  readonly legacy_updated_at: string
  readonly status: AccountStatus
  readonly deleted_at: string | null
  readonly schema_version: number
  readonly source_change_position: string
  readonly source_commit_ts: string
  readonly applied_at: string
}

/**
 * SM2 projection of a legacy holder (`usermanagement.users` +
 * `usermanagement.addresses`). Field-for-field equivalent to the
 * `Holder` schema in `sm2-holder-service`'s `holder-service.yaml` (34
 * fields). Hand-written to mirror the holder OpenAPI rather than pulled
 * through the `gen:api` pipeline, which is wired to a single source (the
 * account service); keep in sync when the holder schema changes.
 *
 * PII-bearing fields are typed nullable per the contract and rendered on
 * the detail page's Holder region behind `MaskedField`. The `vulnerable`
 * / `vulnerability_rating` fields are GDPR Art. 9 special-category data
 * and get a distinct, clearly-labelled treatment in the UI (see
 * `holder-section.tsx`).
 */
export interface Holder {
  // Identifiers + lineage (SM2-owned).
  readonly id: string
  readonly legacy_username: string
  readonly ext_holder_id: string | null
  readonly schema_version: number
  readonly source_change_position: string
  readonly source_commit_ts: string
  readonly applied_at: string
  // Person attributes.
  readonly title: string | null
  readonly first_name: string
  readonly last_name: string
  readonly gender: string | null
  readonly date_of_birth: string | null
  readonly nationality: string | null
  readonly email_address: string | null
  readonly mobile_no: string | null
  readonly dialing_code: string | null
  readonly full_phone_number: string | null
  readonly preferred_language: string | null
  readonly email_notification: boolean
  readonly push_notification: boolean
  readonly sms_notification: boolean
  // Art. 9 special-category fields.
  readonly vulnerable: boolean | null
  readonly vulnerability_rating: string | null
  readonly legacy_user_updated_at: string | null
  // Address attributes (null when the holder has no address row).
  readonly building_name: string | null
  readonly building_no: string | null
  readonly street: string | null
  readonly town: string | null
  readonly postcode: string | null
  readonly county: string | null
  readonly country: string | null
  readonly iso_country_code: string | null
  readonly legacy_address_updated_at: string | null
  // Lifecycle (SM2-owned).
  readonly deleted_at: string | null
}

export type AccountStatus = 'active' | 'suspended' | 'closed' | 'deleted'
export const ACCOUNT_STATUSES: readonly AccountStatus[] = [
  'active',
  'suspended',
  'closed',
  'deleted',
] as const

export interface AccountListPage {
  readonly items: readonly Account[]
  readonly next_cursor: string | null
  readonly prev_cursor: string | null
  readonly result_count: number
  readonly sort: string
  readonly filter: {
    readonly ext_account_id: string | null
    readonly legacy_id: number | string | null
    readonly status: readonly AccountStatus[]
  }
}

export interface ListAccountsParams {
  readonly ext_account_id?: string
  readonly legacy_id?: string
  readonly status?: string
  readonly sort?: string
  readonly cursor?: string
  readonly limit?: number
}

// ---------------------------------------------------------------------------
// Error model.
// ---------------------------------------------------------------------------

export interface BffErrorEnvelope {
  readonly code?: string
  readonly message?: string
  readonly correlation_id?: string
}

export class BffError extends Error {
  readonly status: number
  readonly correlationId: string | null
  readonly envelope: BffErrorEnvelope | null
  readonly kind: 'unauthorised' | 'forbidden' | 'client' | 'server' | 'network'

  constructor(args: {
    status: number
    message: string
    correlationId: string | null
    envelope: BffErrorEnvelope | null
    kind: BffError['kind']
  }) {
    super(args.message)
    this.name = 'BffError'
    this.status = args.status
    this.correlationId = args.correlationId
    this.envelope = args.envelope
    this.kind = args.kind
  }
}

// ---------------------------------------------------------------------------
// Token acquirer abstraction.
// ---------------------------------------------------------------------------

/**
 * Returns a fresh access token. Implementations:
 *   - production: wraps `instance.acquireTokenSilent({ scopes })`.
 *   - test: returns a constant.
 *
 * When silent refresh fails with `InteractionRequiredAuthError`, the
 * implementation should re-throw it untouched so `useBffError` can mount
 * the `SessionExpiredDialog`.
 */
export type AccessTokenAcquirer = () => Promise<string>

// ---------------------------------------------------------------------------
// Client.
// ---------------------------------------------------------------------------

export interface BffClientConfig {
  readonly baseUrl: string
  readonly acquireToken: AccessTokenAcquirer
  /** Optional override for `fetch` (tests inject a fake). */
  readonly fetch?: typeof fetch
}

export interface BffClient {
  listAccounts(params: ListAccountsParams): Promise<AccountListPage>
  getAccount(accountId: string): Promise<Account>
  /**
   * Resolve the holder for an account's `legacy_username` - the primary
   * Murmuration flow (the SPA already holds `account.legacy_username` from
   * `sm2-account-service`). Maps to BFF `GET /holders?legacy_username=`.
   * Resolves to a typed `Holder`; a 404 surfaces as a `BffError` with
   * `status=404` so the Holder region can render its empty state.
   */
  getHolderByUsername(legacyUsername: string): Promise<Holder>
  /**
   * Audit-only POST. The SPA calls this BEFORE painting an unmasked PII
   * value (`reason` omitted or `initial_reveal`) and again on auto-re-mask
   * (`reason` set to `inactivity_30s` / `tab_blur` / `navigation`).
   *
   * On the initial reveal the SPA must await 2xx before painting; on
   * re-mask the SPA fires-and-forgets (the value is hidden either way).
   *
   * `resource` discriminates account vs holder fields (SM2-7); it
   * defaults to `account` so existing account callers are unchanged.
   * `id` is the resource id - an account UUID or a holder UUID.
   */
  postPiiRevealed(args: {
    accountId: string
    field: PiiField
    reason?: PiiRevealAuditReason
    resource?: PiiResource
  }): Promise<{ correlationId: string | null }>
}

/** Account PII field tokens accepted by the BFF's account allow-list. */
export const PII_FIELDS = [
  'legacy_username',
  'bank_account_number',
  'sort_code',
  'iban',
  'tribe_epm_id',
] as const
export type AccountPiiField = (typeof PII_FIELDS)[number]

/**
 * Holder PII field tokens - the masked-by-default (Highly Confidential)
 * set per `01-data-design.md`. Mirrors the BFF holder allow-list. The
 * Art. 9 fields (`vulnerable`, `vulnerability_rating`) are included.
 */
export const HOLDER_PII_FIELDS = [
  'first_name',
  'last_name',
  'date_of_birth',
  'nationality',
  'email_address',
  'mobile_no',
  'full_phone_number',
  'building_name',
  'building_no',
  'street',
  'postcode',
  'vulnerable',
  'vulnerability_rating',
] as const
export type HolderPiiField = (typeof HOLDER_PII_FIELDS)[number]

/** Any field token MaskedField can carry (account or holder). */
export type PiiField = AccountPiiField | HolderPiiField

/** Resource the reveal targets; defaults to `account`. */
export type PiiResource = 'account' | 'holder'

/** Reasons the SPA supplies; `initial_reveal` is the default on omit. */
export type PiiRevealAuditReason =
  | 'initial_reveal'
  | 'inactivity_30s'
  | 'tab_blur'
  | 'navigation'
  | 'user'

export function createBffClient(config: BffClientConfig): BffClient {
  const fetchImpl = config.fetch ?? fetch.bind(globalThis)

  return {
    async listAccounts(params: ListAccountsParams): Promise<AccountListPage> {
      const url = buildUrl(config.baseUrl, '/accounts', params)
      const token = await acquireSafely(config.acquireToken)
      const response = await callFetch(fetchImpl, url, token)
      const correlationId = response.headers.get('x-correlation-id')

      if (!response.ok) {
        throw await mapErrorResponse(response, correlationId)
      }

      // Successful responses are JSON per the BFF contract; a malformed
      // body is surfaced as a 500-class server error rather than crashing
      // the table.
      try {
        const body = (await response.json()) as AccountListPage
        return body
      } catch {
        throw new BffError({
          status: response.status,
          message: 'Malformed BFF response body',
          correlationId,
          envelope: null,
          kind: 'server',
        })
      }
    },

    async getAccount(accountId: string): Promise<Account> {
      // Encode the path segment defensively even though the route guard
      // and react-router route only ever pass an actual UUID. Encoding is
      // free and protects against arbitrary navigation.
      const url = `${config.baseUrl.replace(/\/+$/, '')}/accounts/${encodeURIComponent(accountId)}`
      const token = await acquireSafely(config.acquireToken)
      const response = await callFetch(fetchImpl, url, token)
      const correlationId = response.headers.get('x-correlation-id')

      if (!response.ok) {
        throw await mapErrorResponse(response, correlationId)
      }

      try {
        const body = (await response.json()) as Account
        return body
      } catch {
        throw new BffError({
          status: response.status,
          message: 'Malformed BFF response body',
          correlationId,
          envelope: null,
          kind: 'server',
        })
      }
    },

    async getHolderByUsername(legacyUsername: string): Promise<Holder> {
      const base = config.baseUrl.replace(/\/+$/, '')
      const url = `${base}/holders?legacy_username=${encodeURIComponent(legacyUsername)}`
      const token = await acquireSafely(config.acquireToken)
      const response = await callFetch(fetchImpl, url, token)
      const correlationId = response.headers.get('x-correlation-id')

      if (!response.ok) {
        throw await mapErrorResponse(response, correlationId)
      }

      try {
        const body = (await response.json()) as Holder
        return body
      } catch {
        throw new BffError({
          status: response.status,
          message: 'Malformed BFF response body',
          correlationId,
          envelope: null,
          kind: 'server',
        })
      }
    },

    async postPiiRevealed({ accountId, field, reason, resource }): Promise<{
      correlationId: string | null
    }> {
      const url = `${config.baseUrl.replace(/\/+$/, '')}/audit/pii-revealed`
      const token = await acquireSafely(config.acquireToken)
      // `resource` defaults to account (omitted from the body so existing
      // account behaviour is byte-identical); holder reveals send it.
      const payload: Record<string, string> = { account_id: accountId, field }
      if (resource === 'holder') {
        payload.resource = 'holder'
      }
      if (reason !== undefined && reason !== 'initial_reveal') {
        payload.reason = reason
      }
      const body = JSON.stringify(payload)
      let response: Response
      try {
        response = await fetchImpl(url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body,
          cache: 'no-store',
          credentials: 'omit',
        })
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Network error'
        throw new BffError({
          status: 0,
          message,
          correlationId: null,
          envelope: null,
          kind: 'network',
        })
      }
      const correlationId = response.headers.get('x-correlation-id')
      if (!response.ok) {
        throw await mapErrorResponse(response, correlationId)
      }
      return { correlationId }
    },
  }
}

// ---------------------------------------------------------------------------
// Helpers (exported for unit tests).
// ---------------------------------------------------------------------------

export function buildUrl(baseUrl: string, path: string, params: ListAccountsParams): string {
  // Use a stable, URL-class-compliant base so we get encoding for free.
  // baseUrl may or may not have a trailing slash; normalise it.
  const trimmed = baseUrl.replace(/\/+$/, '')
  const url = new URL(trimmed + path)

  if (params.ext_account_id !== undefined && params.ext_account_id !== '') {
    url.searchParams.set('ext_account_id', params.ext_account_id)
  }
  if (params.legacy_id !== undefined && params.legacy_id !== '') {
    url.searchParams.set('legacy_id', params.legacy_id)
  }
  if (params.status !== undefined && params.status !== '') {
    url.searchParams.set('status', params.status)
  }
  if (params.sort !== undefined) {
    url.searchParams.set('sort', params.sort)
  }
  if (params.cursor !== undefined && params.cursor !== '') {
    url.searchParams.set('cursor', params.cursor)
  }
  if (params.limit !== undefined) {
    url.searchParams.set('limit', String(params.limit))
  }
  return url.toString()
}

async function acquireSafely(acquirer: AccessTokenAcquirer): Promise<string> {
  try {
    return await acquirer()
  } catch (err) {
    // MSAL InteractionRequiredAuthError or similar - re-raise as an
    // unauthorised BffError so the shared error handler can route the user
    // to the SessionExpiredDialog.
    const interaction = err as InteractionRequiredAuthError | Error
    throw new BffError({
      status: 401,
      message: interaction.message || 'Token acquisition failed',
      correlationId: null,
      envelope: null,
      kind: 'unauthorised',
    })
  }
}

async function callFetch(fetchImpl: typeof fetch, url: string, token: string): Promise<Response> {
  try {
    return await fetchImpl(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
      // The BFF sets `Cache-Control: no-store` per ADR-013; we also set
      // `cache: 'no-store'` on the request to harden against any
      // intermediate proxies.
      cache: 'no-store',
      credentials: 'omit',
    })
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Network error'
    throw new BffError({
      status: 0,
      message,
      correlationId: null,
      envelope: null,
      kind: 'network',
    })
  }
}

async function mapErrorResponse(
  response: Response,
  correlationId: string | null,
): Promise<BffError> {
  let envelope: BffErrorEnvelope | null = null
  try {
    const body = (await response.json()) as BffErrorEnvelope
    if (body && typeof body === 'object') {
      envelope = body
    }
  } catch {
    envelope = null
  }

  const kind = classifyKind(response.status)
  const message = envelope?.message ?? response.statusText ?? 'Request failed'
  return new BffError({
    status: response.status,
    message,
    correlationId: correlationId ?? envelope?.correlation_id ?? null,
    envelope,
    kind,
  })
}

function classifyKind(status: number): BffError['kind'] {
  if (status === 401) return 'unauthorised'
  if (status === 403) return 'forbidden'
  if (status >= 400 && status < 500) return 'client'
  return 'server'
}
